<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="90vh")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('record_payment') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

        v-form(ref="form" v-else-if="appointment && appointment.appointments && appointment.appointments.length")
          v-card.round-lg(outlined)
            template(v-for="(appt, index) in appointment.appointments")
              appointment-payment-item.pa-4(:appointment="appt" :index="index" :key="`${appt.id}-${index}`")
              v-divider
            v-row.pa-4(dense align="center")
              v-col(cols="8")
                div.text-xs.neutral500--text.font-semibold.text-right Total
              v-col(cols="4")
                div.text-sm.neutral700--text.font-semibold.text-right {{ $options.filters.displayPrice(totalPrice, $currency) }}

          div.text-sm.font-semibold.neutral500--text.mt-4  {{ $t('payment_method') }}
          y-select.mt-2(v-model="payment.payment_method" :items="paymentMethods" :placeholder="$t('select_payment_method')" :rules="rules.not_empty_select" :loading="isFetching")
          div.text-sm.font-semibold.neutral500--text.mt-4  {{ $t('notes_on_payment') }}
          v-text-field.text-sm.mt-2.round-xl(v-model="payment.internal_description" :placeholder="$t('notes_on_payment_optional')" background-color="neutral100" outlined dense height="48" hide-details="auto")

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed :dark="Boolean(leadProducts.length)" :disabled="Boolean(!leadProducts.length)" @click="confirmAction") {{ $t('confirm') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AppointmentPaymentItem from '@/components/clinic/appointment/advance/AppointmentPaymentItem.vue'

export default {
  components: { AppointmentPaymentItem },
  props: ['open', 'selectedAppointment'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      paymentMethods: [],
      payment: {},
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      appointment: 'clinic/getTempAppointment'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    totalPrice () {
      let total = 0
      if (this.appointment.appointments && this.appointment.appointments.length) {
        total = (this.appointment.appointments.filter(f => f.amount).map(m => m.amount).reduce((total, num) => { return total + num }, 0))
      }
      return total
    },
    leadProducts () {
      let ids = []
      if (this.appointment.appointments && this.appointment.appointments.length) {
        this.appointment.appointments.forEach(o => {
          if (o.leadproducts && o.leadproducts.length) {
            ids = [ ...ids, ...o.leadproducts]
          }
        })
      }
      return ids
    }
  },
  mounted () {
    this.fetchAppointments()
    this.fetchPaymentMethods()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointments () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/appointments/series/${this.selectedAppointment.id}`)
        .then(resp => {
          let respPayload = {
            appointments: [resp.data]
          }
          this.$store.commit('clinic/updateTempAppointment', respPayload)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchPaymentMethods () {
      this.$axios.get(`/clinics/${this.company.slug}/payment_methods`)
        .then(resp => {
          this.paymentMethods = resp.data
        })
        .catch(() => {})
    },
    confirmAction () {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      const data = {
        leadproducts: this.leadProducts,
        payment_method: this.payment.payment_method,
        internal_description: this.payment.internal_description
      }
      const endpoint = `/clinics/${this.company.slug}/appointments/payments`
      this.$axios.post(endpoint, data)
        .then(resp => {
          this.updateActivityLog(endpoint, data, resp, 'POST')
          this.closeResetDialog()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeResetDialog () {
      this.showDialog = false
      this.$emit('reset')
    },
    closeDialog () {
      this.showDialog = false
      this.$emit('closed')
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: 'Record payment combo appointment',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  },
  destroyed () {
    this.$store.commit('clinic/resetTempAppointment')
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>